import './GaugeContainer.scss';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import { Map } from 'immutable';
import Paper from '@mui/material/Paper';
import _ from 'lodash';

import { updateDeviceSettings } from 'src/module/device/action';
import LogoSpinner from 'src/component/UI/LogoSpinner';
import { Grid } from '@mui/material';
import GaugeContent from './GaugeContent';
import { celsiusToFahrenheitConvertor, fahrenheitToCelsiusConvertor } from 'src/utils/utils';
import { getTemperatureUnit } from 'src/module/authentication/selector';
import { deviceTypeConfig } from 'src/utils/deviceTypeConfig';
import { useTranslation } from 'src/i18n';

export default function GaugeContainer (props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    device,
    deviceStatus,
    deviceId,
    configFields,
    gaugeStyle,
    canDisableAllAlarms,
    canDisableControlAlarms,
    enableSpecificAlarms,
    isDeviceInCommFail
  } = props;

  const temperatureUnit = useSelector(getTemperatureUnit);

  let loading = true;
  let hasSettings = false;
  let controlTemperatureSetpoint = device.getIn(['settings', 'controlTemperatureSetpoint']);
  let highTemperatureTripSetpoint = device.getIn(['settings', 'highTemperatureTripSetpoint']);

  let highControlTemperatureAlarmSetpoint = device.getIn(['settings', 'highControlTemperatureAlarmSetpoint']);
  let lowControlTemperatureAlarmSetpoint = device.getIn(['settings', 'lowControlTemperatureAlarmSetpoint']);
  const highLoadCurrentAlarmSetpoint = device.getIn(['settings', 'highLoadCurrentAlarmSetpoint']);
  const lowLoadCurrentAlarmSetpoint = device.getIn(['settings', 'lowLoadCurrentAlarmSetpoint']);
  const highLoadCurrentTripSetpoint = device.getIn(['settings', 'highLoadCurrentTripSetpoint']);
  const highLoadCurrentTripMask = device.getIn(['settings', 'highLoadCurrentTripMask']);

  // Three-phase (HTC3) devices have an independent load-current alarm setpoint/mask per phase.
  const loadCurrentPhases = [
    {
      phase: 'a',
      titleKey: 'gauge.loadCurrentPhaseA',
      modalType: 'GAUGE_LOAD_CURRENT_A',
      highSetpoint: device.getIn(['settings', 'highLoadCurrentAAlarmSetpoint']),
      lowSetpoint: device.getIn(['settings', 'lowLoadCurrentAAlarmSetpoint']),
      highMask: 'highLoadCurrentAAlarmMask',
      lowMask: 'lowLoadCurrentAAlarmMask'
    },
    {
      phase: 'b',
      titleKey: 'gauge.loadCurrentPhaseB',
      modalType: 'GAUGE_LOAD_CURRENT_B',
      highSetpoint: device.getIn(['settings', 'highLoadCurrentBAlarmSetpoint']),
      lowSetpoint: device.getIn(['settings', 'lowLoadCurrentBAlarmSetpoint']),
      highMask: 'highLoadCurrentBAlarmMask',
      lowMask: 'lowLoadCurrentBAlarmMask'
    },
    {
      phase: 'c',
      titleKey: 'gauge.loadCurrentPhaseC',
      modalType: 'GAUGE_LOAD_CURRENT_C',
      highSetpoint: device.getIn(['settings', 'highLoadCurrentCAlarmSetpoint']),
      lowSetpoint: device.getIn(['settings', 'lowLoadCurrentCAlarmSetpoint']),
      highMask: 'highLoadCurrentCAlarmMask',
      lowMask: 'lowLoadCurrentCAlarmMask'
    }
  ];


  const highGroundCurrentAlarmSetpoint = device.getIn(['settings', 'highGroundCurrentAlarmSetpoint']);
  const groundFaultTripCurrentSetpoint = device.getIn(['settings', 'groundFaultTripCurrentSetpoint']);
  const highGroundCurrentAlarmSetpointOptions = device.getIn(['settings', 'highGroundCurrentAlarmSetpointOptions']);
  const groundFaultTripCurrentSetpointOptions = device.getIn(['settings', 'groundFaultTripCurrentSetpointOptions']);
  const controlTemperatureToolTip = device.getIn(['settings', 'controlTemperatureToolTip']);
  const temperatureSensorsToolTip = device.getIn(['settings', 'temperatureSensorsToolTip']);
  const controlSettingsReadonly = device.getIn(['settings', 'controlSettingsReadonly']);
  const readOnlySetpoints = device.getIn(['settings', 'readOnlySetpoints'], []);
  // Handle both regular arrays and Immutable Lists
  const readOnlySetpointsArray = readOnlySetpoints?.toJS ? readOnlySetpoints.toJS() : (Array.isArray(readOnlySetpoints) ? readOnlySetpoints : []);
  const lowLoadCurrentSetpointReadOnly = Boolean(readOnlySetpointsArray && readOnlySetpointsArray.includes('lowLoadCurrentAlarmSetpoint'));
  const settingsToolTip = device.getIn(['settings', 'settingsToolTip'], null);

  const showPrimaryGaugesByDefault = deviceTypeConfig[device.get('type')]?.showPrimaryGaugesByDefault;
  const hideStatus = device.getIn(['settings', 'hideStatus'], !showPrimaryGaugesByDefault);
  const hideGroundFault = device.getIn(['settings', 'hideGroundFaultGauge'], !showPrimaryGaugesByDefault);
  const hideLoadCurrent = device.getIn(['settings', 'hideLoadCurrentGauge'], !showPrimaryGaugesByDefault);
  const hideControlTemp = device.getIn(['settings', 'hideControlTempGauge'], !showPrimaryGaugesByDefault);
  const showInnerPanelTemperature = device.getIn(['settings', 'showInnerPanelTemperatureGauge'], false);

  let innerPanelTemperatureMax = device.getIn(['settings', 'innerPanelTemperatureMax'], false);
  const showStatusBlock = !(hideStatus && hideGroundFault && hideLoadCurrent && hideControlTemp && !showInnerPanelTemperature);

  const temperatureSensorSettings = device.getIn(['settings', 'temperatureSensors']);
  const temperatureSensorStatus = device.getIn(['status', 'temperatureSensors']);

  const temperatureSensors = {};

  if (temperatureSensorSettings && temperatureSensorSettings.size) {
    temperatureSensorSettings.map((value, key) => {
      temperatureSensors[key] = Map({
        settings: value,
        status: temperatureSensorStatus ? temperatureSensorStatus.get(key, null) : null
      });
    });
  }

  // Get isHighTempTripSetpointEnabled from device settings (computed in Elexant3500iDevice)
  const isHighTempTripSetpointEnabled = device.getIn(['settings', 'isHighTempTripSetpointEnabled']);

  let controlTemperature = deviceStatus?.size > 0 ? deviceStatus.get('controlTemperature', null) : null;
  let innerPanelTemperature = deviceStatus?.size > 0 ? deviceStatus.get('innerPanelTemperature', null) : null;
  const loadCurrent = deviceStatus?.size > 0 ? deviceStatus.get('loadCurrent', null) : null;
  // Three-phase devices (e.g. NGC-40 HTC3) report a current per phase. When phase B/C
  // are present we render a load gauge per phase instead of the single load gauge.
  const loadCurrentA = deviceStatus?.size > 0 ? deviceStatus.get('loadCurrentA', null) : null;
  const loadCurrentB = deviceStatus?.size > 0 ? deviceStatus.get('loadCurrentB', null) : null;
  const loadCurrentC = deviceStatus?.size > 0 ? deviceStatus.get('loadCurrentC', null) : null;
  const isThreePhase = loadCurrentB != null || loadCurrentC != null;
  const groundCurrent = deviceStatus?.size > 0 ? deviceStatus.get('groundCurrent', null) : null;
  const circuitOn = device.getIn(['status', 'circuitOn'], false);
  const limiterInstalled = device.getIn(['status', 'limiterInstalled'], false);
  let limiterTemperature = deviceStatus?.size > 0 ? deviceStatus.get('limiterTemperature', null) : null;
  let limiterCutoutSetpoint = device.getIn(['settings', 'limiterCutoutSetpoint'], null);
  const rtdConfigValue = device.getIn(['settings', 'rtdConfig'], null);
  const switchControlModeValue = device.getIn(['settings', 'switchControlMode'], null);
  const [deviceOperatingStatus, setDeviceOperatingStatus] = useState(t('common.na'));

  if (temperatureUnit === 'F') {
    controlTemperatureSetpoint = celsiusToFahrenheitConvertor(controlTemperatureSetpoint);
    highControlTemperatureAlarmSetpoint = celsiusToFahrenheitConvertor(highControlTemperatureAlarmSetpoint);
    lowControlTemperatureAlarmSetpoint = celsiusToFahrenheitConvertor(lowControlTemperatureAlarmSetpoint);
    highTemperatureTripSetpoint = celsiusToFahrenheitConvertor(highTemperatureTripSetpoint);
    controlTemperature = celsiusToFahrenheitConvertor(controlTemperature);
    innerPanelTemperature = celsiusToFahrenheitConvertor(innerPanelTemperature);
    innerPanelTemperatureMax = celsiusToFahrenheitConvertor(innerPanelTemperatureMax);
    limiterTemperature = celsiusToFahrenheitConvertor(limiterTemperature);
    limiterCutoutSetpoint = celsiusToFahrenheitConvertor(limiterCutoutSetpoint);
  }

  let rtdConfigLabel = device.getIn(['settings', 'rtdConfigLabel']) || t('common.na');
  if (rtdConfigLabel === t('common.na')) {
    // if a device has rtd config field in advanced settings
    if (rtdConfigValue != null) {
      const options = configFields?.getIn(['settings', 'rtdConfig', 'options']);
      rtdConfigLabel = options?.find(option => option.get('value') == rtdConfigValue)?.get('label') || rtdConfigLabel;
    }
  }

  // let switchControlModeLabel = 'N/A';
  if (switchControlModeValue != null) {
    //const options = configFields?.getIn(['settings', 'switchControlMode', 'options']);
    // switchControlModeLabel = options?.find(option => option.get('value') == switchControlModeValue)?.get('label') || switchControlModeLabel;
  }

  const handleGaugeSettingChange = (deviceId: string, type: string, id: number = null) => {
    return (values: any) => {
      let rec = null;
      const canDisableAllAlarms = values.canDisableAllAlarms;
      Object.entries(values).forEach(([field, dirtyValue]) => {
        let value = (dirtyValue === null || dirtyValue === '') ? null : +(parseFloat(dirtyValue as string).toFixed(3));

        let obj: any = {};
        const settingsMap = {
          voltage: {
            rangeMin: 'lowVoltageAlarmSetpoint',
            rangeMax: 'highVoltageAlarmSetpoint',
            rangeMinSwitch: 'lowVoltageAlarmMask',
            rangeMaxSwitch: 'highVoltageAlarmMask'
          },
          'load-current': {
            rangeMin: 'lowLoadCurrentAlarmSetpoint',
            rangeMax: 'highLoadCurrentAlarmSetpoint',
            rangeTrip: 'highLoadCurrentTripSetpoint',
            rangeMinSwitch: 'lowLoadCurrentAlarmMask',
            rangeMaxSwitch: 'highLoadCurrentAlarmMask',
            rangeTripSwitch: 'highLoadCurrentTripMask'
          },
          'load-current-a': {
            rangeMin: 'lowLoadCurrentAAlarmSetpoint',
            rangeMax: 'highLoadCurrentAAlarmSetpoint',
            rangeTrip: 'highLoadCurrentTripSetpoint',
            rangeMinSwitch: 'lowLoadCurrentAAlarmMask',
            rangeMaxSwitch: 'highLoadCurrentAAlarmMask'
          },
          'load-current-b': {
            rangeMin: 'lowLoadCurrentBAlarmSetpoint',
            rangeMax: 'highLoadCurrentBAlarmSetpoint',
            rangeTrip: 'highLoadCurrentTripSetpoint',
            rangeMinSwitch: 'lowLoadCurrentBAlarmMask',
            rangeMaxSwitch: 'highLoadCurrentBAlarmMask'
          },
          'load-current-c': {
            rangeMin: 'lowLoadCurrentCAlarmSetpoint',
            rangeMax: 'highLoadCurrentCAlarmSetpoint',
            rangeTrip: 'highLoadCurrentTripSetpoint',
            rangeMinSwitch: 'lowLoadCurrentCAlarmMask',
            rangeMaxSwitch: 'highLoadCurrentCAlarmMask'
          },
          'ground-current': {
            rangeMin: 'highGroundCurrentAlarmSetpoint',
            rangeMax: 'groundFaultTripCurrentSetpoint',
            rangeMinSwitch: 'highGroundFaultCurrentAlarmMask',
            rangeMaxSwitch: 'groundFaultTripAlarmMask'
          },
          'control-temperature': {
            rangeMin: 'lowControlTemperatureAlarmSetpoint',
            rangeMax: 'highControlTemperatureAlarmSetpoint',
            setting: 'controlTemperatureSetpoint',
            rangeTrip: 'highTemperatureTripSetpoint',
            rangeMinSwitch: `lowControlAlarmMask`,
            rangeMaxSwitch: `highControlAlarmMask`,
            isTemp: true
          },
          'limiter': {
            rangeTrip: 'limiterCutoutSetpoint',
            rangeTripSwitch: 'limiterTripAlarmMask',
            isTemp: true
          },
          'temperature-sensor': {
            rangeMin: `temperatureSensors.${id}.lowTemperatureAlarmSetpoint`,
            rangeMax: `temperatureSensors.${id}.highTemperatureAlarmSetpoint`,
            rangeMinSwitch: `lowTS${id}AlarmMask`,
            rangeMaxSwitch: `highTS${id}AlarmMask`,
            isTemp: true
          }
        };

        if (settingsMap[type].isTemp) {
          value = temperatureUnit === 'F' ? fahrenheitToCelsiusConvertor(value) : value;
        }
        if ((canDisableAllAlarms || enableSpecificAlarms?.includes(settingsMap[type][field])) && (field === 'rangeMinSwitch' || field === 'rangeMaxSwitch' || field === 'rangeTripSwitch')) {
          value = dirtyValue ? 1 : 0;
        }
        if ((field.includes('Switch') && (canDisableAllAlarms || enableSpecificAlarms?.includes(settingsMap[type][field]))) || ((field === 'rangeMin' || field === 'rangeMax' || field === 'rangeTrip') && value !== null) || (type === 'control-temperature' && field === 'setting')) {
          const key = settingsMap[type][field];
          // The settings modal always submits every gauge field (rangeMin/Max/Trip and their
          // switches), but each gauge's settingsMap entry only defines the fields it actually
          // uses. A field with no mapping resolves to an undefined key, so skip it rather than
          // writing to `settings.undefined`.
          if (!key) {
            return;
          }
          // Check if this setpoint is in the readOnlySetpoints array - skip if read-only
          const readOnlySetpointsArray = readOnlySetpoints?.toJS ? readOnlySetpoints.toJS() : (Array.isArray(readOnlySetpoints) ? readOnlySetpoints : []);
          if (!readOnlySetpointsArray.includes(key)) {
            if (type === 'temperature-sensor') {
              obj = { settings: { temperatureSensors: {} } };
            }
            obj = _.set(obj, `settings.${key}`, value);
          }
        }

        rec = _.merge(rec, obj);
      });
      if (rec) {
        dispatch(updateDeviceSettings(deviceId, Map(rec)));
      }
    };
  };

  // Renders a load current gauge. Shared by the single-phase gauge and the per-phase
  // gauges (three-phase devices). Each phase has its own setpoint/mask and its own
  // settings-change mapping, so the relevant setting keys are passed in via config.
  const renderLoadGauge = ({ title, currentVal, modalType, highSetpoint, lowSetpoint, highMask, lowMask, settingType }) => (
    <GaugeContent
      deviceId={deviceId}
      title={title}
      unit={'A'}
      canDisableAllAlarms={canDisableAllAlarms}
      enableLowAlarm={enableSpecificAlarms?.includes(lowMask)}
      enableHighAlarm={enableSpecificAlarms?.includes(highMask)}
      intialHighSetpointEnabled={device.getIn(['settings', highMask]) === 1 ? true : false}
      intialLowSetpointEnabled={device.getIn(['settings', lowMask]) === 1 ? true : false}
      rangeMin={lowSetpoint}
      rangeMax={highSetpoint}
      rangeTrip={highLoadCurrentTripSetpoint}
      rangeTripEnableLabel={t('gauge.enableHighLoadCurrentTrip')}
      rangeTripEnabled={highLoadCurrentTripMask !== undefined ? highLoadCurrentTripMask === 1 : highLoadCurrentTripSetpoint != null}
      rangeTripSwitchEditable={settingType === 'load-current' && enableSpecificAlarms?.includes('highLoadCurrentTripMask')}
      rangeTripDisabledLabel={t('gauge.deviceNoSupportHighLoadCurrentTrip')}
      currentVal={currentVal}
      type={modalType}
      key={modalType}
      rangeMinEnableLabel={t('gauge.enableLowLoadAlarm')}
      rangeMaxEnableLabel={t('gauge.enableHighLoadAlarm')}
      rangeMinLabel={t('gauge.lowLoadCurrentAlarmSetpoint')}
      rangeMaxLabel={t('gauge.highLoadCurrentAlarmSetpoint')}
      rangeTripLabel={t('gauge.highLoadCurrentTripSetpoint')}
      rangeMinDisabledLabel={t('gauge.deviceNoSupportLowLoadCurrent')}
      rangeMaxDisabledLabel={t('gauge.deviceNoSupportHighLoadCurrent')}
      handleSettingChange={handleGaugeSettingChange(deviceId, settingType)}
      theme={isDeviceInCommFail ? 'off' : (circuitOn ? 'default' : 'off')}
      gaugeStyle={gaugeStyle}
      readOnlyRangeMin={lowLoadCurrentSetpointReadOnly}
    />
  );

  const renderLoadCurrentGauges = () => {
    if (isThreePhase) {
      return (
        <>
          {[loadCurrentA, loadCurrentB, loadCurrentC].map((phaseCurrent, i) => {
            const config = loadCurrentPhases[i];
            return renderLoadGauge({
              title: t(config.titleKey),
              currentVal: phaseCurrent,
              modalType: config.modalType,
              highSetpoint: config.highSetpoint,
              lowSetpoint: config.lowSetpoint,
              highMask: config.highMask,
              lowMask: config.lowMask,
              settingType: `load-current-${config.phase}`
            });
          })}
        </>
      );
    }
    return renderLoadGauge({
      title: t('gauge.loadCurrent'),
      currentVal: loadCurrent,
      modalType: 'GAUGE_LOAD_CURRENT',
      highSetpoint: highLoadCurrentAlarmSetpoint,
      lowSetpoint: lowLoadCurrentAlarmSetpoint,
      highMask: 'highLoadCurrentAlarmMask',
      lowMask: 'lowLoadCurrentAlarmMask',
      settingType: 'load-current'
    });
  };

  const renderStatusBlock = () => {
    return (
      <div id='gauge-container' data-testid='gauge-container'>
        {loading ?
          <Paper>
            <h3>{t('common.status')}</h3>
            <div className='grid-gauge dummy-gauge'>
              <div className='gauge-overlay'>
                <div className='gauge-large-loading'>
                  <LogoSpinner size={160} />
                </div>
              </div>
              {dummyGauges.map(gauge => {
                return <GaugeContent
                  deviceId={null}
                  key={gauge.type}
                  title={gauge.type}
                  unit={gauge.unit}
                  type={gauge.id}
                  rangeMin={null}
                  rangeMax={null}
                  currentVal={null}
                  rangeMinEnableLabel=''
                  rangeMaxEnableLabel=''
                  rangeMinLabel=''
                  rangeMaxLabel=''
                  gaugeStyle={gaugeStyle}
                />;
              })}
            </div>
          </Paper> :
          hasSettings ?
            <Paper>
              <h3 className='gauge-container-title'>{t('common.status')}</h3>

              {!hideStatus ? (
                <Grid key={12} container spacing={1}>
                  <Grid key={5} item md={6} xs={12} className='device-status'>
                    <h4>{t('gauge.deviceStatus')}</h4>
                    <p>&nbsp;{deviceOperatingStatus}</p>
                  </Grid>
                  <Grid key={789} item md={6} xs={12} className='device-status'>
                    <h4>{t('gauge.rtdConfig')}</h4>
                    <p>&nbsp;{rtdConfigLabel}</p>
                  </Grid>
                </Grid>
              ) : null}

              <div className='grid-gauge'>
                {!hideControlTemp ?
                  <GaugeContent
                    deviceId={deviceId}
                    title={t('gauge.controlTemperature')}
                    unit={temperatureUnit === 'F' ? '°F' : '°C'}
                    rangeMin={lowControlTemperatureAlarmSetpoint}
                    rangeMax={highControlTemperatureAlarmSetpoint}
                    rangeTrip={highTemperatureTripSetpoint}
                    setting={controlTemperatureSetpoint}
                    currentVal={controlTemperature}
                    type={'GAUGE_CONTROL_TEMPERATURE'}
                    key={'GAUGE_CONTROL_TEMPERATURE'}
                    canDisableAllAlarms={canDisableControlAlarms}
                    enableLowAlarm={false}
                    enableHighAlarm={false}
                    intialHighSetpointEnabled={device.getIn(['settings', 'highControlAlarmMask']) === 1 ? true : false}
                    intialLowSetpointEnabled={device.getIn(['settings', 'lowControlAlarmMask']) === 1 ? true : false}
                    rangeMinEnableLabel={t('gauge.enableLowControlTempAlarm')}
                    rangeMaxEnableLabel={t('gauge.enableHighControlTempAlarm')}
                    rangeTripEnableLabel={t('gauge.enableHighTempTripSetpoint')}
                    rangeMinLabel={t('gauge.lowControlTempAlarmSetpoint')}
                    rangeMaxLabel={t('gauge.highControlTempAlarmSetpoint')}
                    rangeTripLabel={t('gauge.highTempTripSetpoint')}
                    rangeMinDisabledLabel={t('gauge.deviceNoSupportLowControlTemp')}
                    rangeMaxDisabledLabel={t('gauge.deviceNoSupportHighControlTemp')}
                    rangeTripEnabled={isHighTempTripSetpointEnabled}
                    settingLabel={t('gauge.controlTempSetpoint')}
                    handleSettingChange={handleGaugeSettingChange(deviceId, 'control-temperature')}
                    gaugeStyle={gaugeStyle}
                    toolTip={controlTemperatureToolTip}
                    theme={isDeviceInCommFail || controlTemperature === null ? 'off' : 'temp'}
                    readOnly={controlSettingsReadonly}
                    settingsToolTips={settingsToolTip}
                  />
                  : null}

                {limiterInstalled ?
                  <GaugeContent
                    deviceId={deviceId}
                    title={t('gauge.limiterTemperature')}
                    unit={temperatureUnit === 'F' ? '°F' : '°C'}
                    rangeTrip={limiterCutoutSetpoint}
                    rangeTripLabel={t('gauge.limiterCutoutSetpoint')}
                    rangeTripEnableLabel={t('gauge.enableLimiterTripAlarm')}
                    rangeTripEnabled={device.getIn(['settings', 'limiterTripAlarmMask']) === 1}
                    rangeTripToolTip={t('gauge.limiterCutoutSetpointTooltip')}
                    rangeTripSwitchEditable
                    currentVal={limiterTemperature}
                    type={'GAUGE_LIMITER_TEMPERATURE'}
                    key={'GAUGE_LIMITER_TEMPERATURE'}
                    canDisableAllAlarms={canDisableAllAlarms}
                    enableLowAlarm={false}
                    enableHighAlarm={false}
                    intialHighSetpointEnabled={false}
                    intialLowSetpointEnabled={false}
                    handleSettingChange={handleGaugeSettingChange(deviceId, 'limiter')}
                    gaugeStyle={gaugeStyle}
                    theme={isDeviceInCommFail ? 'off' : 'temp'}
                  />
                  : null}

                {!hideGroundFault ?
                  <GaugeContent
                    deviceId={deviceId}
                    title={t('gauge.groundFaultCurrent')}
                    unit={'mA'}
                    rangeMin={highGroundCurrentAlarmSetpoint}
                    rangeMax={groundFaultTripCurrentSetpoint}
                    currentVal={groundCurrent}
                    canDisableAllAlarms={canDisableAllAlarms}
                    enableLowAlarm={enableSpecificAlarms?.includes('highGroundFaultCurrentAlarmMask')}
                    enableHighAlarm={enableSpecificAlarms?.includes('groundFaultTripAlarmMask')}
                    intialHighSetpointEnabled={device.getIn(['settings', 'groundFaultTripAlarmMask']) === 1 ? true : false}
                    intialLowSetpointEnabled={device.getIn(['settings', 'highGroundFaultCurrentAlarmMask']) === 1 ? true : false}
                    type={'GAUGE_GROUND_FAULT_CURRENT'}
                    key={'GAUGE_GROUND_FAULT_CURRENT'}
                    rangeMinEnableLabel={t('gauge.enableHighGroundCurrentAlarm')}
                    rangeMaxEnableLabel={t('gauge.enableGroundCurrentTrip')}
                    rangeMinLabel={t('gauge.highGroundCurrentAlarmSetpoint')}
                    rangeMaxLabel={t('gauge.groundFaultTripCurrentSetpoint')}
                    rangeMinDisabledLabel={t('gauge.deviceNoSupportHighGroundCurrent')}
                    rangeMaxDisabledLabel={t('gauge.deviceNoSupportGroundCurrentTrip')}
                    handleSettingChange={handleGaugeSettingChange(deviceId, 'ground-current')}
                    theme={isDeviceInCommFail ? 'off' : 'rising'}
                    gaugeStyle={gaugeStyle}
                    setting={null}
                    rangeMinOptions={highGroundCurrentAlarmSetpointOptions}
                    rangeMaxOptions={groundFaultTripCurrentSetpointOptions}
                  />
                  : null}

                {!hideLoadCurrent && renderLoadCurrentGauges()}

                {showInnerPanelTemperature ?
                  <GaugeContent
                    deviceId={deviceId}
                    title={t('gauge.innerPanelTemperature')}
                    unit={temperatureUnit === 'F' ? '°F' : '°C'}
                    rangeMin={null}
                    rangeMax={innerPanelTemperatureMax}
                    currentVal={innerPanelTemperature}
                    type={'GAUGE_INNER_PANEL_TEMPERATURE'}
                    key={'GAUGE_INNER_PANEL_TEMPERATURE'}
                    canDisableAllAlarms={false}
                    enableLowAlarm={false}
                    enableHighAlarm={false}
                    intialHighSetpointEnabled={false}
                    intialLowSetpointEnabled={false}
                    settingLabel={t('gauge.innerPanelTemperature')}
                    gaugeStyle={gaugeStyle}
                    theme={isDeviceInCommFail ? 'off' : 'temp'}
                    disableHamburger
                  />
                  : null}

                {Object.keys(temperatureSensors).map((key) => {
                  let sensor = temperatureSensors[key];

                  if (!sensor) {
                    sensor = Map();
                  }
                  if (!sensor.getIn(['status', 'temperature'], false) && sensor.getIn(['status', 'temperature']) !== 0 && sensor.getIn(['status', 'temperature']) !== null) {
                    return null;
                  }
                  const lowTemperatureAlarmSetpoint = sensor.getIn(['settings', 'lowTemperatureAlarmSetpoint']);
                  const highTemperatureAlarmSetpoint = sensor.getIn(['settings', 'highTemperatureAlarmSetpoint']);
                  const temperature = sensor.getIn(['status', 'temperature']);
                  return <GaugeContent
                    deviceId={deviceId}
                    key={key}
                    title={t('gauge.temperatureSensor', { number: key })}
                    unit={temperatureUnit === 'F' ? '°F' : '°C'}
                    canDisableAllAlarms={canDisableAllAlarms}
                    enableLowAlarm={enableSpecificAlarms?.includes(`lowTS${key}AlarmMask`)}
                    enableHighAlarm={enableSpecificAlarms?.includes(`highTS${key}AlarmMask`)}
                    intialHighSetpointEnabled={device.getIn(['settings', `highTS${key}AlarmMask`]) === 1 ? true : false}
                    intialLowSetpointEnabled={device.getIn(['settings', `lowTS${key}AlarmMask`]) === 1 ? true : false}
                    rangeMin={temperatureUnit === 'F' && lowTemperatureAlarmSetpoint != null ? celsiusToFahrenheitConvertor(lowTemperatureAlarmSetpoint) : lowTemperatureAlarmSetpoint}
                    rangeMax={temperatureUnit === 'F' && highTemperatureAlarmSetpoint != null ? celsiusToFahrenheitConvertor(highTemperatureAlarmSetpoint) : highTemperatureAlarmSetpoint}
                    currentVal={temperatureUnit === 'F' && temperature != null ? celsiusToFahrenheitConvertor(temperature) : temperature}
                    type={`GAUGE_TEMPERATURE_SENSOR_${key}`}
                    rangeMinEnableLabel={t('gauge.enableLowTempAlarm')}
                    rangeMaxEnableLabel={t('gauge.enableHighTempAlarm')}
                    rangeMinLabel={t('gauge.lowTempAlarmSetpoint')}
                    rangeMaxLabel={t('gauge.highTempAlarmSetpoint')}
                    rangeMinDisabledLabel={t('gauge.deviceNoSupportLowTempAlarm')}
                    rangeMaxDisabledLabel={t('gauge.deviceNoSupportHighTempAlarm')}
                    handleSettingChange={handleGaugeSettingChange(deviceId, 'temperature-sensor', parseInt(key))}
                    gaugeStyle={gaugeStyle}
                    toolTip={device.getIn(['settings', 'excludeSensorsFromTooltip'], []).includes(key) ? null : temperatureSensorsToolTip}
                    theme={isDeviceInCommFail ? 'off' : (sensor.getIn(['status', 'temperature']) === null ? 'off' : 'temp')}
                    settingPrefix={t('gauge.temperatureSensor', { number: key }) + ' '}
                  />;
                })
                }
              </div>
            </Paper>
            : null
        }
      </div>
    );
  };

  if (typeof device.get('settings') !== 'undefined') {
    if (device.get('settings').size > 0) {
      hasSettings = true;
    }
    loading = false;
  }

  const GetCurrentDeviceOperatingStatusLabel = (val: number) => {
    try {
      return configFields.getIn(['settings', 'deviceOperatingStatus', 'options'], [])
        .find(option => option.get('value', null) == val).get('label', null) || null;
    }
    catch {
      return null;
    }
  };

  useEffect(() => {
    const status = GetCurrentDeviceOperatingStatusLabel(device.getIn(['settings', 'deviceOperatingStatus'], null));
    if (deviceOperatingStatus !== status && status) {
      setDeviceOperatingStatus(status);
    }
  }, [device, configFields]);

  const dummyGauges = [
    { type: t('gauge.controlTemperature'), unit: 'C', id: 'GAUGE_CONTROL_TEMPERATURE' },
    { type: t('gauge.groundFaultCurrent'), unit: 'mA', id: 'GAUGE_GROUND_FAULT_CURRENT' },
    { type: t('gauge.loadCurrent'), unit: 'A', id: 'GAUGE_LOAD_CURRENT' },
    { type: t('gauge.temperatureSensor', { number: 1 }), unit: 'C', id: 'GAUGE_TEMPERATURE_SENSOR_1' },
    { type: t('gauge.temperatureSensor', { number: 2 }), unit: 'C', id: 'GAUGE_TEMPERATURE_SENSOR_2' },
    { type: t('gauge.powerConsumption'), unit: 'w', id: 'GAUGE_POWER_CONSUMPTION' }
  ];

  return showStatusBlock ? renderStatusBlock() : null;
}

GaugeContainer.propTypes = {
  device: PropTypes.object,
  deviceStatus: PropTypes.object,
  deviceId: PropTypes.string,
  configFields: PropTypes.object,
  gaugeStyle: PropTypes.string,
  canDisableAllAlarms: PropTypes.bool,
  canDisableControlAlarms: PropTypes.bool,
  isDeviceInCommFail: PropTypes.bool,
  enableSpecificAlarms: PropTypes.object
};

GaugeContainer.defaultProps = {
  device: Map(),
  deviceStatus: Map(),
  deviceId: String,
  configFields: Map(),
  canDisableAllAlarms: false,
  canDisableControlAlarms: false,
  isDeviceInCommFail: false,
  enableSpecificAlarms: null
};
